import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState, useTransition } from "react";
import { usePanelRef, toast, ResizableHandle, ResizablePanel, ResizablePanelGroup, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, Alert, AlertDescription, AlertTitle, Button } from "@ndd/ui";
import { useAuth } from "@clerk/react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import {
  SESSION_PATIENT_LINKS_FLAG,
  useFeatureEnabled,
} from "@/lib/use-features";
import { DeleteSessionDialog } from "@/components/v2/delete-session-dialog";
import { useSessionAnalysis } from "@/lib/use-session-analysis";
import {
  AlertCircle,
  Info,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  RotateCw,
  Trash2,
} from "lucide-react";
import { TranscriptPanel } from "@/components/v2/transcript-panel";
import { AlwaysListScorecard } from "@/components/scorecard/always-list-scorecard";
import { CounselingSkillsScorecard } from "@/components/scorecard/counseling-skills-scorecard";
import { KeyImprovementsSection } from "@/components/narrative/key-improvements-section";
import { MissedOpportunitiesSection } from "@/components/narrative/missed-opportunities-section";
import { RedFlagsSection } from "@/components/narrative/red-flags-section";
import { SectionErrorFallback } from "@/components/narrative/section-error-fallback";
import { SessionActionsSection } from "@/components/narrative/session-actions-section";
import { ThingsDoneWellSection } from "@/components/narrative/things-done-well-section";
import { Badge } from "@/components/badge";
import { SectionPlaceholder } from "@/components/v2/section-placeholder";
import { StatusPill } from "@/components/v2/status-pill";
import { StreamingSection } from "@/components/v2/streaming-section";
import { EditableSessionTitle } from "@/components/v2/editable-session-title";
import { ThinkingPanel } from "@/components/v2/thinking-panel";
import {
  SECTION_DISPLAY_NAMES,
  SECTION_NAMES,
  getNextIncompleteSection,
  type AnalysisResponse,
  type AnalysisStatus,
  type AnalysisSections,
  type SectionName,
  type SessionType,
} from "../../../../shared/analysis-types";
import { formatLocalDate } from "../../../../shared/local-date";
import { PatientLinkChip, type SessionLinkState } from "./patient-link-chip";

interface ScorecardStandaloneProps {
  sessionId: string;
  clientName: string | null;
  sessionDate: string | null;
  sessionType: "initial" | "follow_up" | null;
  promptVersion: string;
  initialStatus: AnalysisStatus;
  initialErrorMessage: string | null;
  rdId: string;
  rdSlug: string;
  rdName: string;
  addedByUserName: string;
  canRerun: boolean;
  patientLink: SessionLinkState;
}

function toSessionType(raw: "initial" | "follow_up" | null): SessionType {
  if (raw === "follow_up") return "follow-up";
  return raw ?? "initial";
}

function apiErrorMessage(error: unknown): string | null {
  if (error instanceof ApiRequestError) {
    const body = error.body as { error?: string } | undefined;
    return body?.error ?? null;
  }
  return null;
}

function renderCompletedSection(
  sectionName: SectionName,
  sections: AnalysisSections,
  sessionType: SessionType,
  promptVersion: string,
) {
  switch (sectionName) {
    case "session_overview": {
      const section = sections.session_overview;
      if (!section) return null;
      return (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
            Session Overview
          </h3>
          {"error" in section ? (
            <div className="mt-4">
              <SectionErrorFallback sectionName={sectionName} />
            </div>
          ) : (
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              {section.text}
            </p>
          )}
        </div>
      );
    }
    case "session_actions": {
      const section = sections.session_actions;
      if (!section) return null;
      return <SessionActionsSection data={section} />;
    }
    case "always_list_scorecard": {
      const section = sections.always_list_scorecard;
      if (!section) return null;
      return (
        <AlwaysListScorecard
          data={section}
          sessionType={sessionType}
          promptVersion={promptVersion}
        />
      );
    }
    case "counseling_skills_scorecard": {
      const section = sections.counseling_skills_scorecard;
      if (!section) return null;
      return (
        <CounselingSkillsScorecard
          data={section}
          promptVersion={promptVersion}
        />
      );
    }
    case "missed_opportunities": {
      const section = sections.missed_opportunities;
      if (!section) return null;
      return <MissedOpportunitiesSection data={section} />;
    }
    case "things_done_well": {
      const section = sections.things_done_well;
      if (!section) return null;
      return <ThingsDoneWellSection data={section} />;
    }
    case "key_improvements": {
      const section = sections.key_improvements;
      if (!section) return null;
      return <KeyImprovementsSection data={section} />;
    }
    case "red_flags": {
      const section = sections.red_flags;
      if (!section) return null;
      return <RedFlagsSection data={section} />;
    }
  }
}

export function ScorecardStandalone({
  sessionId,
  clientName,
  sessionDate,
  sessionType,
  promptVersion,
  initialStatus,
  initialErrorMessage,
  rdId,
  rdSlug,
  rdName,
  addedByUserName,
  canRerun,
  patientLink,
}: ScorecardStandaloneProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const sessionPatientLinksEnabled = useFeatureEnabled(
    SESSION_PATIENT_LINKS_FLAG,
  );
  const { data, loading, timedOut, restart } = useSessionAnalysis(sessionId);
  const [retrying, setRetrying] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [isRerunning, startRerun] = useTransition();

  const handleDeleted = useCallback(() => {
    toast.success("Session deleted");
    void navigate({ to: "/rds/$rdId", params: { rdId: rdSlug }, replace: true });
  }, [navigate, rdSlug]);

  const effectiveData: AnalysisResponse = data ?? {
    status: hasRetried ? "pending" : initialStatus,
    sections: {},
    errorMessage: hasRetried ? undefined : initialErrorMessage ?? undefined,
  };
  const completedSections = effectiveData.sections;
  const currentSection = useMemo(() => {
    if (effectiveData.status !== "analyzing") return null;
    if (!effectiveData.hasStreamingText) return null;
    return getNextIncompleteSection(completedSections);
  }, [completedSections, effectiveData.status, effectiveData.hasStreamingText]);

  const currentSectionName = currentSection
    ? SECTION_DISPLAY_NAMES[currentSection]
    : null;
  const hasAnySections = SECTION_NAMES.some((name) => completedSections[name]);
  const title = clientName || "Untitled Session";
  const formattedDate = formatLocalDate(sessionDate, "shortWithYear");
  const titleWithDate = formattedDate ? `${title} - ${formattedDate}` : title;
  const normalizedSessionType = toSessionType(sessionType);
  const isError = effectiveData.status === "error";
  const showStartCallout =
    !hasAnySections &&
    !isError &&
    !timedOut &&
    (effectiveData.status === "pending" || effectiveData.status === "analyzing");

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await authenticatedApi(getToken, `/api/sessions/${sessionId}/retry`, {
        method: "POST",
      });
      setHasRetried(true);
      restart();
    } catch {
      // Mirrors legacy behavior: a failed retry leaves the error state as-is.
    } finally {
      setRetrying(false);
    }
  }, [getToken, restart, sessionId]);

  const canDelete =
    effectiveData.status !== "pending" && effectiveData.status !== "analyzing";

  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const transcriptPanelRef = usePanelRef();
  const initialTranscriptOpen = String(search.transcript ?? "") === "1";
  const [transcriptCollapsed, setTranscriptCollapsed] = useState(
    !initialTranscriptOpen,
  );
  const [transcriptMounted, setTranscriptMounted] = useState(
    initialTranscriptOpen,
  );

  const toggleTranscript = useCallback(() => {
    const panel = transcriptPanelRef.current;
    if (!panel) return;
    if (transcriptCollapsed) {
      setTranscriptMounted(true);
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [transcriptCollapsed, transcriptPanelRef]);

  const showRerun = canRerun && effectiveData.status === "complete";

  function handleRerunConfirm() {
    setRerunError(null);
    startRerun(async () => {
      try {
        await authenticatedApi(getToken, `/api/sessions/${sessionId}/rerun`, {
          method: "POST",
        });
        setRerunOpen(false);
        toast.success("Rerunning analysis");
        setHasRetried(true);
        restart();
      } catch (error) {
        setRerunError(apiErrorMessage(error) ?? "Failed to rerun analysis.");
      }
    });
  }

  return (
    <ResizablePanelGroup
      orientation="horizontal"
      className="h-full w-full"
      defaultLayout={{ transcript: initialTranscriptOpen ? 40 : 0, scorecard: initialTranscriptOpen ? 60 : 100 }}
    >
      <ResizablePanel
        id="transcript"
        panelRef={transcriptPanelRef}
        defaultSize={initialTranscriptOpen ? "40%" : "0%"}
        minSize="25%"
        maxSize="60%"
        collapsible
        collapsedSize="0%"
        onResize={(size) => {
          const collapsed = size.asPercentage === 0;
          setTranscriptCollapsed(collapsed);
          if (!collapsed) setTranscriptMounted(true);
        }}
        className="overflow-hidden"
      >
        {transcriptMounted && <TranscriptPanel sessionId={sessionId} />}
      </ResizablePanel>
      <ResizableHandle className="w-px bg-sand-200 data-[panel-group-direction=horizontal]:w-px" />
      <ResizablePanel id="scorecard" defaultSize={initialTranscriptOpen ? "60%" : "100%"} minSize="40%">
        <div className="h-full overflow-y-auto">
          <div className="max-w-[800px] mx-auto px-6 py-8 flex flex-col gap-6">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-sand-500">
        <span>AlwaysAI</span>
        <span>&gt;</span>
        <Link to="/rds" className="hover:text-foreground">
          RDs
        </Link>
        <span>&gt;</span>
        <Link
          to="/rds/$rdId"
          params={{ rdId: rdSlug }}
          className="hover:text-foreground"
        >
          {rdName}
        </Link>
        <span>&gt;</span>
        <span className="text-sand-400">{titleWithDate}</span>
      </nav>

      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold font-sans text-foreground flex items-baseline gap-2 flex-wrap">
            <EditableSessionTitle
              sessionId={sessionId}
              initialClientName={clientName}
              fallback="Untitled Session"
              className="text-2xl font-bold font-sans text-foreground"
            />
            {formattedDate && (
              <span className="text-sand-400 font-normal">— {formattedDate}</span>
            )}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-sand-500">
            {sessionType === "initial" ? (
              <Badge variant="category">Initial</Badge>
            ) : sessionType === "follow_up" ? (
              <Badge variant="category">Follow-up</Badge>
            ) : null}
            {sessionPatientLinksEnabled && (
              <PatientLinkChip
                sessionId={sessionId}
                rdId={rdId}
                rdName={rdName}
                patientLink={patientLink}
              />
            )}
            {addedByUserName ? <span>Added by {addedByUserName}</span> : null}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusPill
            status={effectiveData.status}
            currentSectionName={currentSectionName}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleTranscript}
            className="gap-2"
          >
            {transcriptCollapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
            <span>Read Transcript</span>
          </Button>
          {showRerun && (
            <AlertDialog open={rerunOpen} onOpenChange={setRerunOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-sand-400 hover:text-interactive"
                >
                  <RotateCw className="h-4 w-4" />
                  <span className="sr-only">Rerun analysis</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Rerun this analysis?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The current analysis will be replaced with a fresh run
                    using the latest prompt. This may take a few minutes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {rerunError && (
                  <p className="text-sm text-error">{rerunError}</p>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isRerunning}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleRerunConfirm}
                    disabled={isRerunning}
                  >
                    {isRerunning ? "Starting..." : "Rerun"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {canDelete && (
            <DeleteSessionDialog
              sessionId={sessionId}
              onDeleted={handleDeleted}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-sand-400 hover:text-error"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete session</span>
                </Button>
              }
            />
          )}
        </div>
      </header>

      {showStartCallout && (
        <Alert className="rounded-xl border-dashed border-sky-100 bg-sky-100/40 text-foreground">
          <Info className="h-4 w-4 text-interactive" />
          <AlertTitle>Analysis is starting</AlertTitle>
          <AlertDescription className="text-sand-600">
            Analysis usually takes a few minutes. You can keep this page open
            while it runs.
          </AlertDescription>
        </Alert>
      )}

      {timedOut && (
        <Alert className="rounded-xl border-warning-soft bg-card">
          <AlertCircle className="h-4 w-4 text-warning" />
          <AlertTitle>Analysis did not complete</AlertTitle>
          <AlertDescription className="text-sand-600">
            Refresh the page to check whether the analysis recovered.
          </AlertDescription>
        </Alert>
      )}

      <ThinkingPanel
        thinking={effectiveData.thinking}
        status={effectiveData.status}
        firstSectionReady={hasAnySections}
      />


      {isError && (
        <div className="flex flex-col gap-4">
          <Alert variant="danger" className="rounded-xl">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Analysis Error</AlertTitle>
            <AlertDescription>
              {effectiveData.errorMessage ??
                "An error occurred during analysis."}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={handleRetry}
            disabled={retrying}
            className="self-start text-error border-error hover:bg-error-light hover:text-error"
          >
            <RefreshCw className={retrying ? "animate-spin" : ""} />
            Retry
          </Button>
        </div>
      )}

      {loading && !data && !isError ? (
        <div className="flex flex-col gap-4">
          {SECTION_NAMES.map((name) => (
            <SectionPlaceholder
              key={name}
              sectionName={SECTION_DISPLAY_NAMES[name]}
            />
          ))}
        </div>
      ) : !isError || hasAnySections ? (
        <div className="flex flex-col gap-4">
          {SECTION_NAMES.map((name) => {
            const section = completedSections[name];
            const displayName = SECTION_DISPLAY_NAMES[name];

            if (section) {
              return (
                <section
                  key={name}
                  className="rounded-xl border border-sand-200 bg-card p-6"
                >
                  {renderCompletedSection(
                    name,
                    completedSections,
                    normalizedSessionType,
                    promptVersion,
                  )}
                </section>
              );
            }

            if (name === currentSection && !timedOut && !isError) {
              return (
                <StreamingSection key={name} sectionName={displayName} />
              );
            }

            return (
              <SectionPlaceholder key={name} sectionName={displayName} />
            );
          })}
        </div>
      ) : null}
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
